/**
 * ss-store-locator.js — Smoke Shop Store Locator
 * Geocode (Nominatim UK), Haversine miles, Leaflet map, filters.
 */
(function () {
  'use strict';

  var DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
  var DAY_LABELS = {
    mon: 'Monday',
    tue: 'Tuesday',
    wed: 'Wednesday',
    thu: 'Thursday',
    fri: 'Friday',
    sat: 'Saturday',
    sun: 'Sunday'
  };
  var NOMINATIM = 'https://nominatim.openstreetmap.org/search';
  var UA = 'SmokeShopStoreLocator/1.0 (store-locator; contact@smoke-shop.co.uk)';
  var UK_CENTER = [54.5, -2.5];
  var UK_ZOOM = 6;
  var CLUSTER_PX = 42;

  function qs(root, sel) {
    return root.querySelector(sel);
  }

  function qsa(root, sel) {
    return Array.prototype.slice.call(root.querySelectorAll(sel));
  }

  function parseStores(el) {
    if (!el) return [];
    try {
      var data = JSON.parse(el.textContent || '[]');
      if (!Array.isArray(data)) return [];
      return data
        .filter(function (s) {
          return s && s.active !== false;
        })
        .map(function (s) {
          var lat = parseFloat(s.lat);
          var lng = parseFloat(s.lng);
          var services = [];
          if (typeof s.services === 'string' && s.services.trim()) {
            services = s.services
              .split(',')
              .map(function (x) {
                return x.trim();
              })
              .filter(Boolean);
          } else if (Array.isArray(s.services)) {
            services = s.services;
          }
          return {
            id: String(s.id),
            name: s.name || 'Store',
            address1: s.address1 || '',
            address2: s.address2 || '',
            city: s.city || '',
            county: s.county || '',
            postcode: s.postcode || '',
            lat: isFinite(lat) ? lat : null,
            lng: isFinite(lng) ? lng : null,
            phone: s.phone || '',
            email: s.email || '',
            image: s.image || '',
            hours: s.hours || {},
            services: services,
            description: s.description || '',
            distance: null
          };
        });
    } catch (e) {
      return [];
    }
  }

  function formatStoreAddress(store) {
    return [store.address1, store.address2, store.city, store.county, store.postcode, 'United Kingdom']
      .filter(function (part) {
        return part && String(part).trim();
      })
      .join(', ');
  }

  function delay(ms) {
    return new Promise(function (resolve) {
      setTimeout(resolve, ms);
    });
  }

  function haversineMiles(lat1, lng1, lat2, lng2) {
    var R = 3958.7613;
    var toRad = Math.PI / 180;
    var dLat = (lat2 - lat1) * toRad;
    var dLng = (lng2 - lng1) * toRad;
    var a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * toRad) * Math.cos(lat2 * toRad) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  function parseTimeToMinutes(str) {
    if (!str || typeof str !== 'string') return null;
    var m = str.trim().match(/^(\d{1,2}):(\d{2})$/);
    if (!m) return null;
    var h = parseInt(m[1], 10);
    var min = parseInt(m[2], 10);
    if (h > 23 || min > 59) return null;
    return h * 60 + min;
  }

  function isOpenNow(hours, date) {
    date = date || new Date();
    var key = DAY_KEYS[date.getDay()];
    var day = hours && hours[key];
    if (!day) return false;
    var open = parseTimeToMinutes(day.open);
    var close = parseTimeToMinutes(day.close);
    if (open === null || close === null) return false;
    var now = date.getHours() * 60 + date.getMinutes();
    if (close > open) return now >= open && now < close;
    // Overnight e.g. 22:00–02:00
    return now >= open || now < close;
  }

  function formatHoursLine(day) {
    if (!day) return 'Closed';
    var o = day.open && String(day.open).trim();
    var c = day.close && String(day.close).trim();
    if (!o || !c) return 'Closed';
    return o + ' to ' + c;
  }

  function formatAddress(store) {
    return [store.address1, store.address2, store.city, store.county, store.postcode]
      .filter(Boolean)
      .join(', ');
  }

  function formatAddressLines(store) {
    var lines = [];
    if (store.address1) lines.push(store.address1);
    if (store.address2) lines.push(store.address2);
    var cityLine = [store.city, store.county, store.postcode].filter(Boolean).join(', ');
    if (cityLine) lines.push(cityLine);
    return lines;
  }

  function phoneHref(phone) {
    if (!phone) return '';
    var cleaned = String(phone).replace(/[^\d+]/g, '');
    return cleaned ? 'tel:' + cleaned : '';
  }

  function directionsUrl(store) {
    var q = store.lat + ',' + store.lng;
    var isApple = /iPad|iPhone|iPod|Macintosh/.test(navigator.userAgent) && 'ontouchend' in document;
    if (isApple) {
      return 'https://maps.apple.com/?daddr=' + encodeURIComponent(q) + '&dirflg=d';
    }
    return 'https://www.google.com/maps/dir/?api=1&destination=' + encodeURIComponent(q);
  }

  function debounce(fn, wait) {
    var t;
    return function () {
      var ctx = this;
      var args = arguments;
      clearTimeout(t);
      t = setTimeout(function () {
        fn.apply(ctx, args);
      }, wait);
    };
  }

  function escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function Locator(root) {
    this.root = root;
    this.stores = parseStores(qs(root, '[data-ss-stores]'));
    this.origin = null;
    this.selectedId = null;
    this.map = null;
    this.markersLayer = null;
    this.userMarker = null;
    this.markerById = {};
    this.leafletOk = false;
    this.serviceFilters = [];

    this.els = {
      form: qs(root, '[data-ss-search-form]'),
      query: qs(root, '[data-ss-query]'),
      geolocate: qs(root, '[data-ss-geolocate]'),
      radius: qs(root, '[data-ss-radius]'),
      sort: qs(root, '[data-ss-sort]'),
      openNow: qs(root, '[data-ss-open-now]'),
      serviceWrap: qs(root, '[data-ss-service-filters]'),
      status: qs(root, '[data-ss-status]'),
      module: qs(root, '[data-ss-module]'),
      map: qs(root, '[data-ss-map]'),
      mapFallback: qs(root, '[data-ss-map-fallback]'),
      results: qs(root, '[data-ss-results]'),
      count: qs(root, '[data-ss-count]'),
      skeletons: qs(root, '[data-ss-skeletons]'),
      empty: qs(root, '[data-ss-empty]'),
      error: qs(root, '[data-ss-error]'),
      errorText: qs(root, '[data-ss-error-text]'),
      detail: qs(root, '[data-ss-detail]'),
      detailBody: qs(root, '[data-ss-detail-body]'),
      detailClose: qs(root, '[data-ss-detail-close]'),
      sheet: qs(root, '[data-ss-sheet]'),
      sheetBody: qs(root, '[data-ss-sheet-body]'),
      viewToggle: qs(root, '[data-ss-view-toggle]')
    };

    var defR = root.getAttribute('data-default-radius');
    if (defR && this.els.radius) this.els.radius.value = String(defR);

    this.bind();
    this.initMapWhenReady();
    this.prepareStores();
  }

  Locator.prototype.prepareStores = function () {
    var self = this;
    var pending = this.stores.filter(function (s) {
      return s.lat === null || s.lng === null;
    });

    if (!pending.length) {
      this.buildServiceFilters();
      this.renderInitial();
      return;
    }

    this.setLoading(true);
    this.setStatus('Locating stores from addresses…');

    var chain = Promise.resolve();
    pending.forEach(function (store, index) {
      chain = chain.then(function () {
        var query = formatStoreAddress(store);
        if (!query) return delay(0);
        return delay(index > 0 ? 1100 : 0).then(function () {
          return self.geocodeStore(store).then(function (coords) {
            if (coords && isFinite(coords.lat) && isFinite(coords.lng)) {
              store.lat = coords.lat;
              store.lng = coords.lng;
            }
          });
        });
      });
    });

    chain
      .then(function () {
        self.stores = self.stores.filter(function (s) {
          return s.lat !== null && s.lng !== null;
        });
        self.setLoading(false);
        self.buildServiceFilters();
        self.renderInitial();
      })
      .catch(function () {
        self.setLoading(false);
        self.buildServiceFilters();
        self.renderInitial();
      });
  };

  Locator.prototype.setStatus = function (msg, kind) {
    var el = this.els.status;
    if (!el) return;
    el.textContent = msg || '';
    el.classList.remove('is-error', 'is-ok');
    if (kind === 'error') el.classList.add('is-error');
    if (kind === 'ok') el.classList.add('is-ok');
  };

  Locator.prototype.setLoading = function (on) {
    if (this.els.skeletons) this.els.skeletons.hidden = !on;
    if (on && this.els.results) this.els.results.innerHTML = '';
    if (on) {
      if (this.els.empty) this.els.empty.hidden = true;
      if (this.els.error) this.els.error.hidden = true;
    }
  };

  Locator.prototype.showError = function (msg) {
    this.setLoading(false);
    if (this.els.error) this.els.error.hidden = false;
    if (this.els.errorText) this.els.errorText.textContent = msg || 'Please try again.';
    if (this.els.empty) this.els.empty.hidden = true;
    this.setStatus(msg, 'error');
  };

  Locator.prototype.bind = function () {
    var self = this;

    if (this.els.form) {
      this.els.form.addEventListener('submit', function (e) {
        e.preventDefault();
        self.searchQuery();
      });
    }

    if (this.els.query) {
      this.els.query.addEventListener(
        'input',
        debounce(function () {
          /* live suggestion not required; keep debounce ready for future */
        }, 400)
      );
    }

    if (this.els.geolocate) {
      this.els.geolocate.addEventListener('click', function () {
        self.useMyLocation();
      });
    }

    ['radius', 'sort'].forEach(function (key) {
      if (self.els[key]) {
        self.els[key].addEventListener('change', function () {
          self.applyFiltersAndRender();
        });
      }
    });

    if (this.els.openNow) {
      this.els.openNow.addEventListener('change', function () {
        self.applyFiltersAndRender();
      });
    }

    if (this.els.results) {
      this.els.results.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-store-id]');
        if (!btn) return;
        self.selectStore(btn.getAttribute('data-store-id'), true);
      });
      this.els.results.addEventListener('keydown', function (e) {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        var btn = e.target.closest('[data-store-id]');
        if (!btn) return;
        e.preventDefault();
        self.selectStore(btn.getAttribute('data-store-id'), true);
      });
    }

    if (this.els.detailClose) {
      this.els.detailClose.addEventListener('click', function () {
        self.closeDetail();
      });
    }

    qsa(this.root, '[data-ss-sheet-close]').forEach(function (el) {
      el.addEventListener('click', function () {
        self.closeSheet();
      });
    });

    if (this.els.viewToggle) {
      this.els.viewToggle.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-ss-view]');
        if (!btn) return;
        var view = btn.getAttribute('data-ss-view');
        self.setMobileView(view);
      });
    }

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        self.closeSheet();
        self.closeDetail();
      }
    });

    window.addEventListener(
      'resize',
      debounce(function () {
        if (self.map) self.map.invalidateSize();
      }, 200)
    );
  };

  Locator.prototype.setMobileView = function (view) {
    if (!this.els.module) return;
    this.els.module.setAttribute('data-view', view);
    qsa(this.els.viewToggle, '[data-ss-view]').forEach(function (btn) {
      var on = btn.getAttribute('data-ss-view') === view;
      btn.classList.toggle('is-active', on);
      btn.setAttribute('aria-selected', on ? 'true' : 'false');
    });
    var self = this;
    if (this.map && view === 'map') {
      setTimeout(function () {
        self.map.invalidateSize();
      }, 50);
    }
  };

  Locator.prototype.buildServiceFilters = function () {
    if (!this.els.serviceWrap) return;
    var show = this.root.getAttribute('data-show-services') !== 'false';
    if (!show) return;

    var set = {};
    this.stores.forEach(function (s) {
      s.services.forEach(function (svc) {
        set[svc] = true;
      });
    });
    var list = Object.keys(set).sort(function (a, b) {
      return a.localeCompare(b);
    });
    if (!list.length) {
      this.els.serviceWrap.hidden = true;
      return;
    }

    this.els.serviceWrap.hidden = false;
    this.els.serviceWrap.innerHTML = '';
    var self = this;
    list.forEach(function (svc) {
      var label = document.createElement('label');
      label.className = 'ss-locator__chip';
      var input = document.createElement('input');
      input.type = 'checkbox';
      input.value = svc;
      input.setAttribute('data-ss-service', svc);
      label.appendChild(input);
      label.appendChild(document.createTextNode(svc));
      input.addEventListener('change', function () {
        label.classList.toggle('is-on', input.checked);
        self.applyFiltersAndRender();
      });
      self.els.serviceWrap.appendChild(label);
    });
  };

  Locator.prototype.getSelectedServices = function () {
    if (!this.els.serviceWrap) return [];
    return qsa(this.els.serviceWrap, 'input[data-ss-service]:checked').map(function (el) {
      return el.value;
    });
  };

  Locator.prototype.initMapWhenReady = function () {
    var self = this;
    var tries = 0;
    function attempt() {
      tries += 1;
      if (typeof window.L !== 'undefined' && self.els.map) {
        self.initMap();
        return;
      }
      if (tries < 40) {
        setTimeout(attempt, 100);
        return;
      }
      self.leafletOk = false;
      if (self.els.mapFallback) self.els.mapFallback.hidden = false;
    }
    attempt();
  };

  Locator.prototype.initMap = function () {
    try {
      var L = window.L;
      var self = this;
      this.map = L.map(this.els.map, {
        scrollWheelZoom: window.matchMedia('(min-width: 990px)').matches,
        attributionControl: true,
        tap: true
      }).setView(UK_CENTER, UK_ZOOM);

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
      }).addTo(this.map);

      this.markersLayer = L.layerGroup().addTo(this.map);
      this.leafletOk = true;
      this._visibleStores = [];

      this.map.on(
        'zoomend',
        debounce(function () {
          if (self._visibleStores && self._visibleStores.length) {
            self.updateMarkers(self._visibleStores, true);
          }
        }, 150)
      );

      if (this.els.module && !this.els.module.getAttribute('data-view')) {
        this.setMobileView('map');
      }

      var self = this;
      if (typeof ResizeObserver !== 'undefined' && this.els.map) {
        this._mapResizeObs = new ResizeObserver(function () {
          if (self.map) self.map.invalidateSize();
        });
        this._mapResizeObs.observe(this.els.map);
      }

      setTimeout(function () {
        if (self.map) self.map.invalidateSize();
      }, 120);
    } catch (err) {
      this.leafletOk = false;
      if (this.els.mapFallback) this.els.mapFallback.hidden = false;
    }
  };

  Locator.prototype.makeMarkerIcon = function (active) {
    var L = window.L;
    return L.divIcon({
      className: '',
      html:
        '<span class="ss-locator-pin' + (active ? ' is-active' : '') + '" aria-hidden="true">' +
        '<svg viewBox="0 0 24 32" width="30" height="40" fill="none">' +
        '<path d="M12 0C5.37 0 0 5.37 0 12c0 8.4 12 20 12 20s12-11.6 12-20C24 5.37 18.63 0 12 0z" fill="currentColor"/>' +
        '<circle cx="12" cy="12" r="4.4" fill="#fff"/>' +
        '</svg>' +
        '</span>',
      iconSize: [30, 40],
      iconAnchor: [15, 40],
      popupAnchor: [0, -38]
    });
  };

  Locator.prototype.makeClusterIcon = function (count) {
    var L = window.L;
    return L.divIcon({
      className: '',
      html: '<div class="ss-locator-cluster">' + count + '</div>',
      iconSize: [36, 36],
      iconAnchor: [18, 18]
    });
  };

  Locator.prototype.groupNearby = function (stores) {
    if (!this.map || !this.leafletOk) return stores.map(function (s) { return { type: 'single', stores: [s] }; });

    var map = this.map;
    var groups = [];
    var used = {};

    stores.forEach(function (s, i) {
      if (used[s.id]) return;
      var pt = map.latLngToLayerPoint([s.lat, s.lng]);
      var cluster = [s];
      used[s.id] = true;

      for (var j = i + 1; j < stores.length; j++) {
        var o = stores[j];
        if (used[o.id]) continue;
        var pt2 = map.latLngToLayerPoint([o.lat, o.lng]);
        var dx = pt.x - pt2.x;
        var dy = pt.y - pt2.y;
        if (Math.sqrt(dx * dx + dy * dy) < CLUSTER_PX) {
          cluster.push(o);
          used[o.id] = true;
        }
      }

      if (cluster.length === 1) {
        groups.push({ type: 'single', stores: cluster });
      } else {
        groups.push({ type: 'cluster', stores: cluster });
      }
    });

    return groups;
  };

  Locator.prototype.updateMarkers = function (stores, skipFit) {
    if (!this.leafletOk || !this.map || !this.markersLayer) return;
    var L = window.L;
    var self = this;
    this._visibleStores = stores;
    this.markersLayer.clearLayers();
    this.markerById = {};

    var groups = this.groupNearby(stores);

    groups.forEach(function (g) {
      if (g.type === 'single') {
        var store = g.stores[0];
        var marker = L.marker([store.lat, store.lng], {
          icon: self.makeMarkerIcon(store.id === self.selectedId),
          title: store.name,
          keyboard: true,
          riseOnHover: true
        });
        marker.on('click', function () {
          self.selectStore(store.id, true);
        });
        marker.addTo(self.markersLayer);
        self.markerById[store.id] = marker;
      } else {
        var lat = 0;
        var lng = 0;
        g.stores.forEach(function (s) {
          lat += s.lat;
          lng += s.lng;
        });
        lat /= g.stores.length;
        lng /= g.stores.length;

        // At higher zoom, spiderfy nearby pins; otherwise show a count cluster
        if (self.map.getZoom() >= 14) {
          g.stores.forEach(function (store, idx) {
            var angle = (idx / g.stores.length) * Math.PI * 2;
            var offset = 0.00022;
            var spider = L.marker(
              [store.lat + Math.cos(angle) * offset, store.lng + Math.sin(angle) * offset],
              {
                icon: self.makeMarkerIcon(store.id === self.selectedId),
                title: store.name,
                keyboard: true
              }
            );
            spider.on('click', function () {
              self.selectStore(store.id, true);
            });
            spider.addTo(self.markersLayer);
            self.markerById[store.id] = spider;
          });
        } else {
          var cluster = L.marker([lat, lng], {
            icon: self.makeClusterIcon(g.stores.length),
            keyboard: true,
            title: g.stores.length + ' stores'
          });
          cluster.on('click', function () {
            var bounds = L.latLngBounds(
              g.stores.map(function (s) {
                return [s.lat, s.lng];
              })
            );
            self.map.fitBounds(bounds.pad(0.45), { maxZoom: 16 });
          });
          cluster.addTo(self.markersLayer);
        }
      }
    });

    if (skipFit) return;

    if (stores.length === 1) {
      this.map.setView([stores[0].lat, stores[0].lng], 13);
    } else if (stores.length > 1) {
      var bounds = L.latLngBounds(
        stores.map(function (s) {
          return [s.lat, s.lng];
        })
      );
      if (this.origin) bounds.extend([this.origin.lat, this.origin.lng]);
      this.map.fitBounds(bounds.pad(0.15), { maxZoom: 13 });
    } else if (this.origin) {
      this.map.setView([this.origin.lat, this.origin.lng], 11);
    }
  };

  Locator.prototype.setUserMarker = function (lat, lng) {
    if (!this.leafletOk || !this.map) return;
    var L = window.L;
    if (this.userMarker) {
      this.userMarker.setLatLng([lat, lng]);
      return;
    }
    this.userMarker = L.marker([lat, lng], {
      icon: L.divIcon({
        className: '',
        html: '<div class="ss-locator-user"></div>',
        iconSize: [16, 16],
        iconAnchor: [8, 8]
      }),
      interactive: false,
      keyboard: false
    }).addTo(this.map);
  };

  Locator.prototype.filterStores = function () {
    var radius = parseFloat(this.els.radius && this.els.radius.value) || 25;
    var sort = (this.els.sort && this.els.sort.value) || 'nearest';
    var openOnly = this.els.openNow && this.els.openNow.checked;
    var services = this.getSelectedServices();
    var origin = this.origin;
    var list = this.stores.slice();

    list.forEach(function (s) {
      if (origin) {
        s.distance = haversineMiles(origin.lat, origin.lng, s.lat, s.lng);
      } else {
        s.distance = null;
      }
    });

    if (origin) {
      list = list.filter(function (s) {
        return s.distance !== null && s.distance <= radius;
      });
    }

    if (openOnly) {
      list = list.filter(function (s) {
        return isOpenNow(s.hours);
      });
    }

    if (services.length) {
      list = list.filter(function (s) {
        return services.every(function (svc) {
          return s.services.indexOf(svc) !== -1;
        });
      });
    }

    if (sort === 'az') {
      list.sort(function (a, b) {
        return a.name.localeCompare(b.name);
      });
    } else if (origin) {
      list.sort(function (a, b) {
        return (a.distance || 0) - (b.distance || 0);
      });
    } else {
      list.sort(function (a, b) {
        return a.name.localeCompare(b.name);
      });
    }

    return list;
  };

  Locator.prototype.renderInitial = function () {
    if (!this.stores.length) {
      if (this.els.empty) this.els.empty.hidden = false;
      if (this.els.count) this.els.count.textContent = '0 stores';
      this.setStatus(
        'Add store blocks in the theme editor with address and postcode. Coordinates are looked up automatically.',
        'error'
      );
      return;
    }
    this.applyFiltersAndRender();
    this.setStatus('Showing all stores. Search a location or use your location to sort by distance.');
  };

  Locator.prototype.applyFiltersAndRender = function () {
    if (this.els.error) this.els.error.hidden = true;
    var list = this.filterStores();
    this.renderList(list);
    this.updateMarkers(list);

    if (!list.length) {
      if (this.els.empty) {
        this.els.empty.hidden = false;
        var title = qs(this.els.empty, '.ss-locator__empty-title');
        var text = qs(this.els.empty, '.ss-locator__empty-text');
        if (this.stores.length) {
          if (title) title.textContent = 'No stores match your search';
          if (text) {
            text.textContent = this.origin
              ? 'Try a larger radius, clear filters, or search a different area.'
              : 'Try adjusting filters, or search by postcode, town or city.';
          }
        }
      }
      if (this.els.count) this.els.count.textContent = '0 stores';
    } else if (this.els.empty) {
      this.els.empty.hidden = true;
    }
  };

  Locator.prototype.renderList = function (list) {
    var self = this;
    var ul = this.els.results;
    if (!ul) return;
    ul.innerHTML = '';

    if (this.els.count) {
      this.els.count.textContent = list.length + (list.length === 1 ? ' store' : ' stores');
    }

    list.forEach(function (store) {
      var li = document.createElement('li');
      var open = isOpenNow(store.hours);
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'ss-locator__card' + (store.id === self.selectedId ? ' is-selected' : '');
      btn.setAttribute('data-store-id', store.id);
      btn.setAttribute('aria-pressed', store.id === self.selectedId ? 'true' : 'false');

      var distHtml = '';
      if (store.distance !== null && isFinite(store.distance)) {
        distHtml =
          '<span class="ss-locator__dist">' +
          (store.distance < 10 ? store.distance.toFixed(1) : Math.round(store.distance)) +
          ' mi</span>';
      }

      var thumbHtml = store.image
        ? '<img class="ss-locator__card-thumb" src="' +
          escapeHtml(store.image) +
          '" alt="' +
          escapeHtml(store.name) +
          '" loading="lazy" width="72" height="72">'
        : '<span class="ss-locator__card-thumb ss-locator__card-thumb--ph" aria-hidden="true">' +
          '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9l1-5h16l1 5"/><path d="M4 9v11h16V9"/><path d="M9 20v-6h6v6"/></svg>' +
          '</span>';

      btn.innerHTML =
        thumbHtml +
        '<div class="ss-locator__card-main">' +
        '<div class="ss-locator__card-top">' +
        '<p class="ss-locator__card-name">' +
        escapeHtml(store.name) +
        '</p>' +
        '<span class="ss-locator__badge ss-locator__badge--' +
        (open ? 'open' : 'closed') +
        '">' +
        (open ? 'Open now' : 'Closed') +
        '</span>' +
        '</div>' +
        '<p class="ss-locator__card-addr">' +
        escapeHtml(formatAddress(store)) +
        '</p>' +
        '<div class="ss-locator__card-meta">' +
        distHtml +
        (store.phone ? '<span>' + escapeHtml(store.phone) + '</span>' : '') +
        '</div>' +
        '</div>';

      li.appendChild(btn);
      ul.appendChild(li);
    });
  };

  Locator.prototype.detailHtml = function (store) {
    var open = isOpenNow(store.hours);
    var todayKey = DAY_KEYS[new Date().getDay()];
    var addrLines = formatAddressLines(store)
      .map(function (l) {
        return escapeHtml(l);
      })
      .join('<br>');

    var hoursHtml = '<ul class="ss-locator__hours">';
    ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'].forEach(function (key) {
      hoursHtml +=
        '<li class="' +
        (key === todayKey ? 'is-today' : '') +
        '"><span class="ss-locator__hours-day">' +
        DAY_LABELS[key] +
        '</span><span>' +
        escapeHtml(formatHoursLine(store.hours[key])) +
        '</span></li>';
    });
    hoursHtml += '</ul>';

    var servicesHtml = '';
    if (store.services.length) {
      servicesHtml =
        '<ul class="ss-locator__services-list">' +
        store.services
          .map(function (s) {
            return '<li>' + escapeHtml(s) + '</li>';
          })
          .join('') +
        '</ul>';
    }

    var imgHtml = store.image
      ? '<img class="ss-locator__detail-img" src="' +
        escapeHtml(store.image) +
        '" alt="' +
        escapeHtml(store.name) +
        '" loading="lazy" width="160" height="160">'
      : '<div class="ss-locator__detail-img ss-locator__detail-img--ph" aria-hidden="true">Store</div>';

    var actions =
      '<div class="ss-locator__detail-actions">' +
      '<a class="ss-locator__btn ss-locator__btn--primary ss-locator__btn--sm" href="' +
      escapeHtml(directionsUrl(store)) +
      '" target="_blank" rel="noopener noreferrer">Directions</a>' +
      (store.phone
        ? '<a class="ss-locator__btn ss-locator__btn--ghost ss-locator__btn--sm" href="' +
          escapeHtml(phoneHref(store.phone)) +
          '">Call</a>'
        : '') +
      (store.email
        ? '<a class="ss-locator__btn ss-locator__btn--ghost ss-locator__btn--sm" href="mailto:' +
          escapeHtml(store.email) +
          '">Email</a>'
        : '') +
      '</div>';

    return (
      '<div class="ss-locator__detail-grid">' +
      imgHtml +
      '<div>' +
      '<span class="ss-locator__badge ss-locator__badge--' +
      (open ? 'open' : 'closed') +
      '">' +
      (open ? 'Open now' : 'Closed') +
      '</span>' +
      '<h3 class="ss-locator__detail-name">' +
      escapeHtml(store.name) +
      '</h3>' +
      '<p class="ss-locator__detail-addr">' +
      addrLines +
      '</p>' +
      actions +
      '<p class="ss-locator__filter-label">Opening hours</p>' +
      hoursHtml +
      servicesHtml +
      (store.description
        ? '<p class="ss-locator__detail-desc">' + escapeHtml(store.description) + '</p>'
        : '') +
      '</div></div>'
    );
  };

  Locator.prototype.selectStore = function (id, pan) {
    var store = null;
    for (var i = 0; i < this.stores.length; i++) {
      if (this.stores[i].id === id) {
        store = this.stores[i];
        break;
      }
    }
    if (!store) return;

    this.selectedId = id;
    qsa(this.els.results, '[data-store-id]').forEach(function (btn) {
      var on = btn.getAttribute('data-store-id') === id;
      btn.classList.toggle('is-selected', on);
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    });

    // Refresh marker active state for visible filtered set (keep current viewport)
    var visible = this.filterStores();
    this.updateMarkers(visible, true);

    if (pan && this.map && this.leafletOk) {
      this.map.setView([store.lat, store.lng], Math.max(this.map.getZoom(), 14), { animate: true });
    }

    var html = this.detailHtml(store);
    var isDesktop = window.matchMedia('(min-width: 990px)').matches;

    if (isDesktop) {
      if (this.els.detailBody) this.els.detailBody.innerHTML = html;
      if (this.els.detail) this.els.detail.hidden = false;
      this.closeSheet();
    } else {
      if (this.els.sheetBody) this.els.sheetBody.innerHTML = html;
      if (this.els.sheet) this.els.sheet.hidden = false;
      document.body.classList.add('ss-locator-open');
      document.body.style.overflow = 'hidden';
    }
  };

  Locator.prototype.closeDetail = function () {
    if (this.els.detail) this.els.detail.hidden = true;
  };

  Locator.prototype.closeSheet = function () {
    if (this.els.sheet) this.els.sheet.hidden = true;
    document.body.classList.remove('ss-locator-open');
    document.body.style.overflow = '';
  };

  Locator.prototype.geocodeStore = function (store) {
    var self = this;
    var postcode = (store.postcode || '').trim();
    if (postcode) {
      var clean = postcode.replace(/\s+/g, '').toUpperCase();
      return fetch('https://api.postcodes.io/postcodes/' + encodeURIComponent(clean))
        .then(function (res) {
          return res.json();
        })
        .then(function (data) {
          if (data && data.status === 200 && data.result) {
            return {
              lat: parseFloat(data.result.latitude),
              lng: parseFloat(data.result.longitude)
            };
          }
          return null;
        })
        .catch(function () {
          return null;
        })
        .then(function (coords) {
          if (coords) return coords;
          return self.geocodeAddress(formatStoreAddress(store));
        });
    }
    return this.geocodeAddress(formatStoreAddress(store));
  };

  Locator.prototype.geocodeAddress = function (query) {
    if (!query) return Promise.resolve(null);
    return this.geocode(query).then(function (results) {
      if (results && results[0]) {
        return {
          lat: parseFloat(results[0].lat),
          lng: parseFloat(results[0].lon)
        };
      }
      return null;
    });
  };

  Locator.prototype.geocode = function (query) {
    var url =
      NOMINATIM +
      '?format=json&limit=1&countrycodes=gb&q=' +
      encodeURIComponent(query);

    // Browsers forbid custom User-Agent; identify politely via Referer + Accept.
    return fetch(url, {
      headers: {
        Accept: 'application/json'
      },
      referrerPolicy: 'origin-when-cross-origin'
    }).then(function (res) {
      if (!res.ok) throw new Error('Geocoding failed');
      return res.json();
    });
  };

  Locator.prototype.searchQuery = function () {
    var self = this;
    var q = this.els.query && this.els.query.value.trim();
    if (!q) {
      this.setStatus('Enter a postcode, town or city to search.', 'error');
      if (this.els.query) this.els.query.focus();
      return;
    }

    this.setLoading(true);
    this.setStatus('Searching…');

    this.geocode(q)
      .then(function (results) {
        self.setLoading(false);
        if (!results || !results.length) {
          self.showError('We could not find that location. Try a full UK postcode or town name.');
          return;
        }
        var r = results[0];
        self.origin = {
          lat: parseFloat(r.lat),
          lng: parseFloat(r.lon)
        };
        self.setUserMarker(self.origin.lat, self.origin.lng);
        self.applyFiltersAndRender();
        var n = self.filterStores().length;
        self.setStatus(
          n
            ? 'Found ' + n + ' store' + (n === 1 ? '' : 's') + ' near ' + (r.display_name || q) + '.'
            : 'No stores within the selected radius. Try increasing the radius.',
          n ? 'ok' : 'error'
        );
      })
      .catch(function () {
        self.setLoading(false);
        self.showError('Location lookup failed. Please try again in a moment.');
      });
  };

  Locator.prototype.useMyLocation = function () {
    var self = this;
    if (!navigator.geolocation) {
      this.setStatus('Location is not supported in this browser. Enter a postcode instead.', 'error');
      return;
    }

    this.setLoading(true);
    this.setStatus('Getting your location…');

    navigator.geolocation.getCurrentPosition(
      function (pos) {
        self.setLoading(false);
        self.origin = {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude
        };
        self.setUserMarker(self.origin.lat, self.origin.lng);
        self.applyFiltersAndRender();
        var n = self.filterStores().length;
        self.setStatus(
          n
            ? 'Found ' + n + ' store' + (n === 1 ? '' : 's') + ' near you.'
            : 'No stores within the selected radius. Try increasing the radius.',
          n ? 'ok' : 'error'
        );
      },
      function (err) {
        self.setLoading(false);
        var msg = 'We could not access your location. Enter a postcode instead.';
        if (err && err.code === 1) {
          msg = 'Location permission denied. Enter a postcode, town or city instead.';
        } else if (err && err.code === 3) {
          msg = 'Location request timed out. Please try again or enter a postcode.';
        }
        self.setStatus(msg, 'error');
      },
      { enableHighAccuracy: false, timeout: 12000, maximumAge: 60000 }
    );
  };

  function boot() {
    qsa(document, '[data-ss-locator]').forEach(function (root) {
      if (root.getAttribute('data-ss-ready')) return;
      root.setAttribute('data-ss-ready', '1');
      new Locator(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
